/**
 * Resolving an emailed link.
 *
 * Supabase's client is configured with `detectSessionInUrl`, which means it
 * consumes the credential in the URL *itself*, during initialisation, before
 * any component renders. Calling `exchangeCodeForSession` afterwards fails —
 * the code is single-use and has already been spent — which surfaces to the
 * user as "that link is not valid" on a link that was perfectly valid.
 *
 * So the job here is to **observe**, not to exchange: wait for the client to
 * settle, then ask whether a session exists. Manual exchange is kept only as a
 * fallback for the case where automatic detection did not fire.
 *
 * This also handles every shape a Supabase link arrives in, because which one
 * you get depends on the project's settings and its email template, and a
 * person clicking a link should not have to care:
 *
 *   - `?token_hash=…&type=signup` — the template using `{{ .TokenHash }}`.
 *     Verified here explicitly. **The only shape that survives being opened in
 *     a different browser from the one that registered**, which is the usual
 *     way an activation link is opened: a webmail client, or a phone.
 *   - `?code=…` — PKCE. The exchange needs the code verifier that `signUp`
 *     left in the *registering* browser's storage, so this shape can only be
 *     completed there.
 *   - `#access_token=…` — the implicit flow, consumed by the client on init.
 *   - an error in the query string or the hash, depending on where in the flow
 *     Supabase gave up.
 */

import type { EmailOtpType } from '@supabase/supabase-js';
import { supabaseClient } from './auth.ts';

export type LinkOutcome =
  | { status: 'signed-in' }
  /** The link carried an error from Supabase; `message` is fit to display. */
  | { status: 'failed'; message: string }
  /** No credential in the URL at all — the user arrived directly. */
  | { status: 'no-credential' };

/**
 * The URL this page was opened with, captured at module load.
 *
 * The Supabase client strips the credential out of the address bar as soon as
 * it has consumed it, and it does that on a network round trip whose timing has
 * nothing to do with React's. A component reading `window.location.href` inside
 * an effect is therefore racing it, and losing that race looks exactly like an
 * invalid link. Module bodies run before any of that: `auth.ts` is imported
 * above, so its client exists, but nothing it does asynchronously can have
 * happened yet.
 */
export const arrivalHref: string = typeof window === 'undefined' ? '' : window.location.href;

/** What, if anything, an emailed link is carrying. */
export type LinkCredential = 'token-hash' | 'code' | 'hash-token' | 'error' | 'none';

export function linkCredential(href: string): LinkCredential {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return 'none';
  }
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));

  // An error takes precedence: a link can carry both a spent code and the
  // reason it was refused, and the reason is the more useful thing to say.
  if (url.searchParams.has('error') || url.searchParams.has('error_description')) return 'error';
  if (hash.has('error') || hash.has('error_description')) return 'error';

  if (url.searchParams.has('token_hash') || url.searchParams.has('token')) return 'token-hash';
  if (url.searchParams.has('code')) return 'code';
  if (hash.has('access_token')) return 'hash-token';
  return 'none';
}

/**
 * How long to wait for the client to consume the URL before giving up.
 *
 * Short, because `getSession()` already awaits the client's initialisation —
 * which is where `detectSessionInUrl` does its work — so the first answer is
 * normally the final one. The remaining margin covers a client constructed
 * after this call. Every second beyond that is a second somebody spends
 * watching "Activating your account…" before being told what to do instead.
 */
const SETTLE_TIMEOUT_MS = 1500;

/**
 * Waits for an emailed link to produce a session.
 *
 * Resolves as soon as a session appears rather than after a fixed delay, so the
 * common case is fast and the slow case still terminates.
 */
export async function resolveAuthLink(href: string, settleMs: number = SETTLE_TIMEOUT_MS): Promise<LinkOutcome> {
  const client = supabaseClient();
  if (!client) return { status: 'failed', message: 'Accounts are not available on this deployment.' };

  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  const credential = linkCredential(href);

  if (credential === 'error') {
    const description =
      url.searchParams.get('error_description') ??
      hash.get('error_description') ??
      url.searchParams.get('error') ??
      hash.get('error') ??
      '';
    return { status: 'failed', message: humanise(description) };
  }

  if (credential === 'none') {
    // Still check for an existing session: a user who already activated and
    // clicked the link a second time should not be told it failed.
    const { data } = await client.auth.getSession();
    return data.session ? { status: 'signed-in' } : { status: 'no-credential' };
  }

  /*
   * A token hash is ours to spend, and spending it needs nothing but the link.
   *
   * Unlike PKCE, no secret was left behind in the browser that registered — so
   * this is the path that works when the mail is read on a phone, in a webmail
   * client, or in any of the other browsers a link is opened in. Nothing else
   * consumes it, so it is verified here rather than waited for.
   */
  if (credential === 'token-hash') {
    const tokenHash = url.searchParams.get('token_hash') ?? (url.searchParams.get('token') as string);
    const type = (url.searchParams.get('type') ?? 'signup') as EmailOtpType;
    const { data, error } = await client.auth.verifyOtp({ token_hash: tokenHash, type });
    if (!error && data.session) return { status: 'signed-in' };
    // A second attempt on the same hash fails, and an effect that ran twice is
    // the ordinary reason for that. Believe the session, not the error.
    return (await sessionOutcome(client)) ?? { status: 'failed', message: humanise(error?.message ?? '') };
  }

  const session = await waitForSession(client, settleMs);
  if (session) return { status: 'signed-in' };

  // Automatic detection did not fire. Exchanging manually is safe now, because
  // nothing has consumed the code.
  const code = url.searchParams.get('code');
  if (code) {
    const { data, error } = await client.auth.exchangeCodeForSession(code);
    if (!error && data.session) return { status: 'signed-in' };
    if (error) {
      return (await sessionOutcome(client)) ?? { status: 'failed', message: humanise(error.message) };
    }
  }

  return (
    (await sessionOutcome(client)) ?? {
      status: 'failed',
      message: 'That link could not be used. It may have expired or already been used.',
    }
  );
}

/** `signed-in` if a session exists after all, so a stale error is not reported over it. */
async function sessionOutcome(client: NonNullable<ReturnType<typeof supabaseClient>>): Promise<LinkOutcome | null> {
  const { data } = await client.auth.getSession();
  return data.session ? { status: 'signed-in' } : null;
}

/**
 * Resolves with the session as soon as one exists, or null on timeout.
 *
 * Polls rather than relying solely on `onAuthStateChange`, because the client
 * may already have finished before this runs and would then never emit.
 */
async function waitForSession(
  client: NonNullable<ReturnType<typeof supabaseClient>>,
  timeoutMs: number,
): Promise<unknown | null> {
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    const { data } = await client.auth.getSession();
    if (data.session) return data.session;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

/** Turns Supabase's wording into something worth showing a person. */
function humanise(message: string): string {
  const lower = message.toLowerCase();

  if (lower.includes('expired') || lower.includes('otp_expired')) {
    // An hour is the project's `mailer_otp_exp`, not a guess. Saying "a day"
    // when it is an hour sends somebody looking for a different explanation.
    return 'That link has expired. Links last an hour and can be used once — including by an email provider that opens them for you.';
  }
  if (lower.includes('already') && lower.includes('confirm')) {
    return 'This account is already active — you can sign in.';
  }
  /*
   * The cross-browser PKCE failure, in the words the server uses for it.
   *
   * `signUp` leaves a code verifier in the browser it ran in, and only that
   * browser can complete a `?code=` link. Reading the mail anywhere else — a
   * phone, a webmail tab in another browser, a mail app's own viewer — lands
   * here through no fault of the person clicking, so the message says what to
   * do rather than implying they broke something.
   */
  if (lower.includes('code verifier') || lower.includes('flow state')) {
    return 'That link has to be opened in the browser you registered in. Send yourself a new one below and open it here, or sign in if the account is already active.';
  }
  if (lower.includes('invalid') || lower.includes('not found')) {
    return 'That link is not valid. It may already have been used.';
  }
  if (message.trim() === '') {
    return 'That link could not be used. It may have expired or already been used.';
  }
  // Supabase encodes spaces as + in query strings.
  return message.replace(/\+/g, ' ');
}
